import React, { createContext, useContext, useState, useEffect } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next';

interface ImpersonationContextType {
  originalAdminToken: string | null;
  startImpersonation: (token: string) => void;
  stopImpersonation: () => void;
  isImpersonating: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [originalAdminToken, setOriginalAdminToken] = useState<string | null>(null);
  const { t } = useTranslation();
  const { logout, loginWithCustomToken } = useAuth() as any; // Cast as any because we'll add loginWithCustomToken to AuthContext

  useEffect(() => {
    // Check if we have a stored token on mount (page refresh support)
    const storedToken = localStorage.getItem('impersonation_admin_token');
    if (storedToken) {
      setOriginalAdminToken(storedToken);
    }
  }, []);

  const startImpersonation = (token: string) => {
    setOriginalAdminToken(token);
    localStorage.setItem('impersonation_admin_token', token);
  };

  const stopImpersonation = async () => {
    if (originalAdminToken) {
      try {
        await logout();
        // Re-login as admin using the stored token
        // Note: Firebase custom tokens are for one-time use or have short expiry.
        // Storing the ID token might not be enough to re-auth fully if it expires.
        // A better approach for "Stop Impersonation" is usually:
        // 1. Logout
        // 2. Redirect to Login (User has to login again as Admin for security)
        // OR
        // Use a persistent refresh token mechanism if available, but for security,
        // asking the admin to re-login is safer and simpler.
        
        // Let's just clear state and redirect to login for now, or if we want to be fancy,
        // we can try to reuse the token if it's still valid, but signInWithCustomToken takes a custom token, not ID token.
        // signInWithCredential takes AuthCredential.
        
        // Simpler secure path:
        localStorage.removeItem('impersonation_admin_token');
        setOriginalAdminToken(null);
        window.location.href = '/login'; 
      } catch (error) {
        console.error("Error stopping impersonation", error);
      }
    }
  };

  return (
    <ImpersonationContext.Provider value={{
      originalAdminToken,
      startImpersonation,
      stopImpersonation,
      isImpersonating: !!originalAdminToken
    }}>
      {children}
      {originalAdminToken && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          bg="red.500"
          color="white"
          p={4}
          borderRadius="md"
          boxShadow="lg"
          zIndex={9999}
        >
          <Flex align="center" direction="column" gap={2}>
            <Text fontWeight="bold" color="white">🕵️ Impersonation Mode</Text>
            <Button size="sm" colorScheme="red" variant="outline" onClick={stopImpersonation}>
              Stop & Logout
            </Button>
          </Flex>
        </Box>
      )}
    </ImpersonationContext.Provider>
  );
};
